import type { TFunction } from "i18next";

import type { DeliveryQuery, RedeliveryCounts, WebhookDelivery } from "../schema/webhookDelivery";

export type DeliveryFilters = {
	status: NonNullable<DeliveryQuery["status"]> | "";
	from: string;
	to: string;
};
export const EMPTY_FILTERS: DeliveryFilters = { status: "", from: "", to: "" };

/** Date-only selections include the entire local calendar day, including DST transitions. */
export function deliveryDateBound(value: string, end = false, time?: string): string | undefined {
	if (!value) {
		return undefined;
	}

	const clock = time
		? `${time}:${end ? "59.999" : "00.000"}`
		: end
			? "23:59:59.999"
			: "00:00:00.000";
	const date = new Date(`${value}T${clock}`);

	return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
export function deliveryQuery(
	webhookId: number,
	page: number,
	take: number,
	filters: DeliveryFilters
): DeliveryQuery {
	return {
		webhookId,
		page,
		take,
		...(filters.status ? { status: filters.status } : {}),
		...(filters.from ? { from: deliveryDateBound(filters.from) } : {}),
		...(filters.to ? { to: deliveryDateBound(filters.to, true) } : {}),
	};
}
export const deliveryTime = (value: string) =>
	new Date(value).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
export function deliveryStatusDetail(row: WebhookDelivery, t: TFunction): string {
	if (row.givenUpKind === "NOT_SENT") {
		return t("notSent");
	}

	if (row.status === "QUEUED") {
		return t("queued");
	}

	if (row.givenUpKind === "FINAL_ANSWER") {
		return t("finalAnswer");
	}

	if (row.nextAttemptAt && row.status === "RETRYING") {
		return t("nextAttempt", { date: deliveryTime(row.nextAttemptAt) });
	}

	return row.givenUpAt ? t("givenUp", { date: deliveryTime(row.givenUpAt) }) : "";
}
/** The wait before an attempt, as the detail's attempt table shows it: minutes, then hours. */
export function deliveryWait(previous: string, current: string, t: TFunction): string {
	const minutes = Math.round((Date.parse(current) - Date.parse(previous)) / 60000);

	return minutes < 60
		? t("after", { minutes })
		: t("afterHours", { hours: Math.round(minutes / 60) });
}
/** How long the receiver took to answer: milliseconds under a second, then seconds. */
export function deliveryDuration(durationMs: number | null): string {
	if (durationMs == null) {
		return "–";
	}

	return durationMs < 1000 ? `${durationMs} ms` : `${(durationMs / 1000).toFixed(1)} s`;
}
export function deliveryAttempts(row: WebhookDelivery, t: TFunction): string {
	if (row.givenUpKind === "NOT_SENT") {
		return t("notSent");
	}

	if (row.givenUpKind === "FINAL_ANSWER") {
		return t("ofOne", { count: row.attemptCount });
	}

	return row.status === "DELIVERED"
		? String(row.attemptCount)
		: t("ofSix", { count: row.attemptCount });
}
export function redeliveryResult(result: RedeliveryCounts, t: TFunction): string {
	const skipped = Object.values(result.skipped).reduce(
		(total, value) => total + (Number(value) || 0),
		0
	);

	return t("result", { resend: Number(result.resend), count: Number(result.resend), skipped });
}
export function redeliveryError(error: unknown, t: TFunction): string {
	const data = (error as { data?: { code?: string; count?: number; max?: number } })?.data;
	let reason: string;

	switch (data?.code) {
		case "WEBHOOK_REDELIVERY_PAUSED":
			reason = t("paused");
			break;
		case "WEBHOOK_REDELIVERY_RUNNING":
			reason = t("running", { count: Number(data.count) });
			break;
		case "WEBHOOK_REDELIVERY_TOO_MANY":
			reason = t("overLimit", { count: Number(data.count), max: Number(data.max) });
			break;
		default:
			return t("uncertain");
	}

	return t("nothing", { reason });
}
